// FILE: pullRequestPresentation.tsx
// Purpose: How a pull request looks, in the one place the list and the detail panel both read it
//          from — state glyph, mergeability, author chip and label dots.
// Layer: Presentation helpers for the Pull Requests section
// Exports: pullRequestStateDisplay, pullRequestMergeabilityDisplay, PullRequestAuthorChip,
//          PullRequestLabelDots, isViewerAuthored
//
// Extracted before the detail panel exists rather than after: the glyph that says "merged" has to
// be the same glyph in the row and in the panel, and the colours are not new — they are the ones
// `prStatusIndicator` already uses for the PR badge on a thread in the sidebar. A second palette
// here would make the same pull request look like two different things in two places.

import type {
  PullRequestActor,
  PullRequestChecksState,
  PullRequestErrorReason,
  PullRequestLabel,
  PullRequestListEntry,
  PullRequestMergeability,
  PullRequestState,
} from "@taskin-code/contracts";
import {
  CircleAlertIcon,
  CircleCheckIcon,
  GitMergeIcon,
  GitPullRequestClosedIcon,
  GitPullRequestDraftIcon,
  GitPullRequestIcon,
  LoaderCircleIcon,
  type LucideIcon,
} from "~/lib/icons";
import { Avatar, AvatarFallback, AvatarImage } from "~/components/ui/avatar";
import { cn } from "~/lib/utils";

export interface PullRequestStateDisplay {
  readonly Icon: LucideIcon;
  /** Read out by assistive tech and used as the glyph's tooltip. */
  readonly label: string;
  readonly colorClass: string;
}

/**
 * Draft wins over open on purpose: a draft *is* open, but "open" is a claim that somebody may
 * review it, and that is exactly what a draft is not.
 */
export function pullRequestStateDisplay(entry: {
  readonly state: PullRequestState;
  readonly isDraft: boolean;
}): PullRequestStateDisplay {
  if (entry.state === "merged") {
    return {
      Icon: GitMergeIcon,
      label: "Merged",
      colorClass: "text-violet-600 dark:text-violet-300/90",
    };
  }
  if (entry.state === "closed") {
    return {
      Icon: GitPullRequestClosedIcon,
      label: "Closed",
      colorClass: "text-zinc-500 dark:text-zinc-400/80",
    };
  }
  if (entry.isDraft) {
    return {
      Icon: GitPullRequestDraftIcon,
      label: "Draft",
      colorClass: "text-muted-foreground/70",
    };
  }
  return {
    Icon: GitPullRequestIcon,
    label: "Open",
    colorClass: "text-emerald-600 dark:text-emerald-300/90",
  };
}

/**
 * Only conflicts are worth a mark. "mergeable" is the ordinary case and would be noise on every
 * row, and "unknown" is GitHub still computing it — drawing that as a warning would put a red
 * flag on healthy pull requests for as long as it takes GitHub to answer.
 */
export function pullRequestMergeabilityDisplay(
  mergeability: PullRequestMergeability,
): { readonly label: string; readonly className: string } | null {
  return mergeability === "conflicting"
    ? {
        label: "Conflicts",
        className: "border-amber-500/30 bg-amber-500/10 text-amber-600 dark:text-amber-300/90",
      }
    : null;
}

/**
 * Whether this row is the reader's own work, for the project it belongs to. The login comes from
 * that project's own token, so two projects can legitimately answer with two different people —
 * which is why the lookup is by project and not a single "me".
 */
export function isViewerAuthored(
  entry: PullRequestListEntry,
  viewers: Readonly<Record<string, string>>,
): boolean {
  const viewer = viewers[entry.projectId];
  if (!viewer || !entry.author) return false;
  return viewer.toLowerCase() === entry.author.login.toLowerCase();
}

function initialsOf(login: string): string {
  return login.slice(0, 2).toUpperCase();
}

/** Avatar plus login. Falls back to initials for an account with no avatar, and to a dash for a
 * pull request whose author's account is gone — which GitHub reports as no user at all. */
export function PullRequestAuthorChip({
  author,
  className,
}: {
  author: PullRequestActor | null;
  className?: string;
}) {
  if (!author) {
    return <span className={cn("text-muted-foreground/70", className)}>unknown author</span>;
  }
  return (
    <span className={cn("inline-flex min-w-0 items-center gap-1.5", className)}>
      <Avatar className="size-4">
        {author.avatarUrl ? <AvatarImage src={author.avatarUrl} alt="" /> : null}
        <AvatarFallback className="bg-muted text-[8px] text-muted-foreground">
          {initialsOf(author.login)}
        </AvatarFallback>
      </Avatar>
      <span className="truncate">{author.login}</span>
    </span>
  );
}

const MAX_VISIBLE_LABELS = 3;

/**
 * Labels as coloured dots with their names, not as filled chips. GitHub's colours are arbitrary
 * six-digit values chosen against GitHub's own background; painting them across a row here is how
 * a list ends up with unreadable text in one theme or the other. The dot carries the colour, the
 * name stays on the app's own foreground tokens.
 */
export function PullRequestLabelDots({
  labels,
  className,
}: {
  labels: ReadonlyArray<PullRequestLabel>;
  className?: string;
}) {
  if (labels.length === 0) return null;
  const visible = labels.slice(0, MAX_VISIBLE_LABELS);
  const overflow = labels.length - visible.length;

  return (
    <span className={cn("inline-flex min-w-0 items-center gap-2", className)}>
      {visible.map((label) => (
        <span key={label.name} className="inline-flex min-w-0 items-center gap-1">
          <span
            aria-hidden
            className="size-1.5 shrink-0 rounded-full bg-muted-foreground/40"
            style={label.color ? { backgroundColor: `#${label.color}` } : undefined}
          />
          <span className="truncate">{label.name}</span>
        </span>
      ))}
      {overflow > 0 ? <span className="shrink-0">+{overflow}</span> : null}
    </span>
  );
}

// ── Checks ───────────────────────────────────────────────────────────

export interface PullRequestChecksDisplay {
  readonly Icon: LucideIcon;
  readonly label: string;
  readonly colorClass: string;
}

/**
 * The one-glyph verdict on CI. Only ever drawn from a detail read: the listing does not carry a
 * rollup, and asking for one per row would be a request per row — the cost this whole section is
 * shaped to avoid. `undefined` means the repository reported no check runs at all, which is not
 * "pending" and must not draw a spinner that never resolves.
 */
export function pullRequestChecksDisplay(
  checksState: PullRequestChecksState | undefined,
): PullRequestChecksDisplay | null {
  if (checksState === undefined) return null;
  if (checksState === "passing") {
    return {
      Icon: CircleCheckIcon,
      label: "Checks passing",
      colorClass: "text-emerald-600 dark:text-emerald-300/90",
    };
  }
  if (checksState === "failing") {
    return {
      Icon: CircleAlertIcon,
      label: "Checks failing",
      colorClass: "text-destructive",
    };
  }
  return {
    Icon: LoaderCircleIcon,
    label: "Checks running",
    colorClass: "text-amber-600 dark:text-amber-300/90",
  };
}

// ── Failures ─────────────────────────────────────────────────────────

export interface PullRequestErrorGuidance {
  /** What went wrong, in four words. */
  readonly title: string;
  /** What the reader can do about it. Empty for the failures nobody can act on. */
  readonly guidance: string;
}

/**
 * A typed reason, read as an instruction.
 *
 * This is the whole point of the server answering with `PullRequestErrorReason` instead of a
 * sentence: "no funciona" sends somebody to ask in chat, "this project has no GitHub token" sends
 * them to the one screen that fixes it. Two of the six are actionable in the app, one is a wait,
 * one is a token scope, one is a project that was never on GitHub, and one is genuinely unknown —
 * and each of those is a different next step.
 */
export function describePullRequestErrorReason(
  reason: PullRequestErrorReason,
): PullRequestErrorGuidance {
  switch (reason) {
    case "no-credential":
      return {
        title: "No GitHub token",
        guidance:
          "Add a GitHub token for this project in its settings, under the Git section, and it will appear here.",
      };
    case "credential-invalid":
      return {
        title: "GitHub rejected the token",
        guidance:
          "The stored token has expired or been revoked. Replace it in this project's Git settings.",
      };
    case "forbidden":
      return {
        title: "Token cannot see this repository",
        guidance:
          "The token is valid but has no access here. Give it the `repo` scope, or use one belonging to somebody who can read this repository.",
      };
    case "not-github":
      return {
        title: "Not a GitHub repository",
        guidance:
          "This project's remote does not point at GitHub, so there are no pull requests to read.",
      };
    case "rate-limited":
      return {
        title: "GitHub rate limit reached",
        guidance:
          "Nothing to fix — the limit resets on its own, usually within the hour. Refreshing sooner only spends what is left of it.",
      };
    case "upstream":
      return {
        title: "GitHub could not answer",
        guidance: "",
      };
  }
}

/**
 * The readable half of a websocket failure.
 *
 * Every `pullRequests.*` method except the diff answers a failure with `Cause.pretty`, which is
 * `"Error: <message>"` followed by a *server-side* stack trace. The message underneath is written
 * to be shown — see the server's `Errors.ts` — but the reason is flattened out of it and the
 * stack is noise from a machine the reader has never seen. So: first line, prefix removed, and
 * nothing invented about which failure it was.
 */
export function readPullRequestErrorMessage(error: unknown, fallback: string): string {
  const raw = error instanceof Error ? error.message : typeof error === "string" ? error : "";
  const firstLine = raw.split("\n", 1)[0]?.trim() ?? "";
  const withoutPrefix = firstLine.replace(/^(?:Error|PullRequestError):\s*/, "").trim();
  return withoutPrefix.length > 0 ? withoutPrefix : fallback;
}
